<template>
  <div v-if="projectDataReady && projectData" class="content-with-spacing">
    <div class="heading-container">
      <h1>
        <i class="fas fa-file-alt" aria-hidden="true"></i>
        {{ $route.name === 'project_review_evidence_detail' && currentEvidenceCrumbName
          ? `Review Evidence: ${currentEvidenceCrumbName}`
          : `Review ${projectData.name}` }}
      </h1>
      <span class="sub-heading-container">
        <router-link
          :to="{
            name: 'dashboard_students',
            params: { studentId: projectData.user_id }
          }"
          class="learner-link"
        >
          {{ learnerName }}
        </router-link>
        - {{ formattedId }}
      </span>
    </div>
    <k-nav-tabs v-if="showTabs" :tabs="tabs" :current-tab="currentTab"/>
    <router-view :project-data="projectData" @project-updated="getProject" @set-evidence-crumb="setEvidenceCrumb" ref="routerView"></router-view>
  </div>
</template>

<style scoped>
.heading-container {
  margin-bottom: 30px;
  display: flex;
  flex-direction: column;
}

.sub-heading-container {
  margin: 0 15px;
  display: flex;
  gap: 5px;
}
</style>

<script>
import KNavTabs from '@base-components/k-nav-tabs.vue';
import { formatId } from '@utils/format-user-id';
import PageReadyMixin from '@mixins/page-ready-mixin';

export default {
  name: 'ProjectReviewPage',
  components: {
    KNavTabs,
  },
  mixins: [PageReadyMixin],

  props: {
    projectId: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      projectDataReady: false,
      projectData: null,
      currentEvidenceCrumbName: null,
      learnerName: '',
      tabs: [
        {
          name: 'Scope',
          routeName: 'project_review_scope',
          routeParams: {
            projectId: this.projectId,
          },
        },
        {
          name: 'Evidence',
          routeName: 'project_review_evidence',
          routeParams: {
            projectId: this.projectId,
          },
        },
      ],
    };
  },

  beforeMount() {
    this.$Loading.start();
    this.getProject();
  },

  computed: {
    ready() {
      return this.projectDataReady;
    },
    currentTab() {
      return this.$route.name;
    },
    showTabs() {
      return this.$route.name !== 'project_review_evidence_detail';
    },
    formattedId() {
      return formatId(this.projectData.user_id);
    },
  },

  watch: {
    projectData() {
      this.registerCrumbs();
    },
    $route() {
      // Re-register breadcrumbs when switching tab scope/evidence
      if (this.projectData) {
        this.registerCrumbs();
      }
      if (this.$route.name !== 'project_review_evidence_detail') {
        this.currentEvidenceCrumbName = null;
      }
    },
  },

  methods: {
    registerCrumbs() {
      const crumbs = [
        { text: 'Project Inbox', path: { name: 'project_inbox' } },
      ];

      const onEvidenceDetail = this.$route.name === 'project_review_evidence_detail';
      const projectCrumb = {
        text: this.projectData.name,
        active: !onEvidenceDetail,
      };
      if (onEvidenceDetail) {
        projectCrumb.path = { name: 'project_review_evidence', params: { projectId: this.projectId } };
      }
      crumbs.push(projectCrumb);

      if (onEvidenceDetail && this.currentEvidenceCrumbName) {
        crumbs.push({ text: this.currentEvidenceCrumbName, active: true });
      }

      this.$crumbs.register(crumbs);
    },

    setEvidenceCrumb(name) {
      this.currentEvidenceCrumbName = name || null;
      this.registerCrumbs();
    },

    getLearnerName() {
      if (!this.projectData || !this.projectData.user_id) {
        return Promise.resolve();
      }

      return this.$http.get(`/api/profile/users/${this.projectData.user_id}`)
        .then(res => {
          this.learnerName = res.data.full_name;
        })
        .catch(() => {
          this.learnerName = 'Unknown User';
        });
    },

    setDefaultTab() {
      if (this.$route.name === 'project_review_page') {
        const defaultTab = this.projectData.scoping_completed ? 'project_review_evidence' : 'project_review_scope';
        this.$router.replace({ name: defaultTab });
      }
    },

    async getProject() {
      this.projectDataReady = false;
      this.$logger.info('Fetching project data', { projectId: this.projectId });
      try {
        const response = await this.$http.get(`/api/curriculum/programmes/projects/${this.projectId}`);
        this.$logger.info('Successfully fetched project data', { projectId: this.projectId });
        this.projectData = response.data;
        await this.getLearnerName();
        this.setDefaultTab();
      } catch (error) {
        this.$logger.error('Error fetching project data', { projectId: this.projectId }, error);
        this.$ktoast.showError(error, true);
      } finally {
        this.projectDataReady = true;
      }
    },
  },
};
</script>
